import { combineReducers } from 'redux';
import { DEFAULT_GOOGLE_MAPS_SETTINGS } from '~/_shared/components/google-map/google-map.component';
import { type LatLng } from '../../../_shared/types/latLng';
import { type BoundingBox } from '../../../map/map/boundingBox';
import { MAP_RESET } from '../../map/map.actionTypes';
import { type MapComponentAction } from './mapComponent.action';
import {
  MAP_COMPONENT_SET_CENTER,
  MAP_COMPONENT_SET_CENTER_AND_ZOOM,
  MAP_COMPONENT_SET_LAST_BOUNDS,
  MAP_COMPONENT_SET_ZOOM,
  MAP_COMPONENT_SET_ZOOM_TO_BOUNDS,
} from './mapComponent.actionTypes';
import { areLastBoundsEqual } from './mapComponent.helpers';
import {
  type LastBounds, type MapAuthoringOps, type MapComponentState,
} from './mapComponent.state';
import { mapComponentOptionsReducer } from './mapComponentOptions/mapComponentOptions.reducer';

const initialAuthoringOps: MapAuthoringOps = {
  center: null,
  zoom: null,
  bounds: null,
};

const initialState: Pick<
  MapComponentState,
  'center' | 'zoom' | 'authoringCenter' | 'authoringZoom' | 'zoomToBounds' | 'zoomToBoundsPreferredZoom' | 'lastBounds' | 'authoringOps'
> = {
  center: DEFAULT_GOOGLE_MAPS_SETTINGS.center,
  zoom: DEFAULT_GOOGLE_MAPS_SETTINGS.zoom,
  authoringCenter: null,
  authoringZoom: null,
  lastBounds: null,
  zoomToBounds: null,
  zoomToBoundsPreferredZoom: null,
  authoringOps: initialAuthoringOps,
};

// Current map zoom — reflects whatever was last dispatched, including sync
// self-reports from the map's `idle` listener.
const zoom = (state = initialState.zoom, action: MapComponentAction): number | null => {
  switch (action.type) {
    case MAP_COMPONENT_SET_ZOOM:
      return action.payload.zoom;

    case MAP_COMPONENT_SET_CENTER_AND_ZOOM:
      return action.payload.zoom;

    default: {
      return state;
    }
  }
};

// Current map center — same semantics as `zoom` above.
const center = (state = initialState.center, action: MapComponentAction): LatLng => {
  switch (action.type) {
    case MAP_COMPONENT_SET_CENTER:
      return {
        ...action.payload.center,
      };

    case MAP_COMPONENT_SET_CENTER_AND_ZOOM:
      return {
        ...action.payload.center,
      };

    default: {
      return state;
    }
  }
};

// Authoring center — the position `useMapFeatures` will call `map.setCenter`
// with. Only updated when the incoming action carries a non-null `centerOpId`
// (i.e. `resetMap: true`). Sync dispatches (`resetMap: false`, `centerOpId ===
// null`) leave this untouched so a pending author cannot be squashed by a
// self-report from the map's idle listener.
const authoringCenter = (state = initialState.authoringCenter, action: MapComponentAction): LatLng | null => {
  switch (action.type) {
    case MAP_COMPONENT_SET_CENTER:
    case MAP_COMPONENT_SET_CENTER_AND_ZOOM:
      return action.payload.centerOpId ? { ...action.payload.center } : state;

    default: {
      return state;
    }
  }
};

const authoringZoom = (state = initialState.authoringZoom, action: MapComponentAction): number | null => {
  switch (action.type) {
    case MAP_COMPONENT_SET_ZOOM:
    case MAP_COMPONENT_SET_CENTER_AND_ZOOM:
      return action.payload.zoomOpId ? action.payload.zoom : state;

    default: {
      return state;
    }
  }
};

// `zoomToBounds` is authoring-only by design: nobody dispatches
// SET_ZOOM_TO_BOUNDS as a sync self-report from the map (the map reports its
// idle viewport via SET_LAST_BOUNDS, not SET_ZOOM_TO_BOUNDS). So a single slice
// is enough. Still gated on `boundsOpId` for consistency with the other
// authoring slices.
const zoomToBounds = (state = initialState.zoomToBounds, action: MapComponentAction): BoundingBox | null => {
  switch (action.type) {
    case MAP_COMPONENT_SET_ZOOM_TO_BOUNDS:
      return action.payload.boundsOpId ? action.payload.boundingBox : state;

    default: {
      return state;
    }
  }
};

const zoomToBoundsPreferredZoom = (state = initialState.zoomToBoundsPreferredZoom, action: MapComponentAction): number | null => {
  switch (action.type) {
    case MAP_COMPONENT_SET_ZOOM_TO_BOUNDS:
      return action.payload.boundsOpId ? (action.payload.preferredZoom ?? null) : state;

    default: {
      return state;
    }
  }
};

const lastBounds = (state = initialState.lastBounds, action: MapComponentAction): LastBounds | null => {
  switch (action.type) {
    case MAP_COMPONENT_SET_LAST_BOUNDS: {
      const newBounds = {
        bounds: action.payload.boundingBox,
        zoomLevel: action.payload.zoomLevel,
      };
      if (areLastBoundsEqual(state, newBounds)) {
        return state;
      }
      return newBounds;
    }

    default: {
      return state;
    }
  }
};

// A single reducer manages all three op tags. SET_CENTER_AND_ZOOM can advance
// both `center` and `zoom` tags in one action, which is why the tags live in a
// nested object instead of separate slices.
const authoringOps = (state = initialState.authoringOps, action: MapComponentAction): MapAuthoringOps => {
  switch (action.type) {
    case MAP_COMPONENT_SET_CENTER:
      return action.payload.centerOpId
        ? { ...state, center: action.payload.centerOpId }
        : state;

    case MAP_COMPONENT_SET_ZOOM:
      return action.payload.zoomOpId
        ? { ...state, zoom: action.payload.zoomOpId }
        : state;

    case MAP_COMPONENT_SET_CENTER_AND_ZOOM: {
      const { centerOpId, zoomOpId } = action.payload;
      if (!centerOpId && !zoomOpId) {
        return state;
      }
      return {
        ...state,
        ...(centerOpId ? { center: centerOpId } : null),
        ...(zoomOpId ? { zoom: zoomOpId } : null),
      };
    }

    case MAP_COMPONENT_SET_ZOOM_TO_BOUNDS:
      return action.payload.boundsOpId
        ? { ...state, bounds: action.payload.boundsOpId }
        : state;

    default: {
      return state;
    }
  }
};

const regularReducer = combineReducers({
  zoom,
  center,
  authoringCenter,
  authoringZoom,
  zoomToBounds,
  zoomToBoundsPreferredZoom,
  lastBounds,
  authoringOps,
  mapOptions: mapComponentOptionsReducer,
});

export const mapComponentReducer = (state: MapComponentState, action: MapComponentAction | Action) => {
  if (action.type === MAP_RESET) {
    return regularReducer(undefined, action);
  }

  return regularReducer(state, action);
};
